import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import {
    LuxButton,
    LuxDataTable,
    LuxBadgeLabel,
    LuxIcon,
    LUX_INPUT_TYPE,
    LUX_TEMPLATE_TYPE,
} from '@lux/wardrobe-lib'
import {
    ActivitySummaryChartData,
    NEW_ACTIVITY_APPROVAL_REDUCER_NAMESPACE,
} from '@/app/redux/reducers/activity-approval'
import { setSelectedStores } from '@/app/redux/actions/activity-approval'
import { StoreProps } from '@/app/models/store'
import ActivitySummaryChart from '../../UI/ActivitySummaryChart/ActivitySummaryChart'
import './ActionApproval.scss'
import { statusColors } from '@/app/utils/statusColorMap'
import { FilterMatchMode } from 'primereact/api'
import { downloadExcelFromList } from '@/app/utils/commonFunctions'
import { NEW_ACTIVITY_EXECUTION_NAMESPACE } from '@/app/redux/reducers/activity-execution'
import {
    canAcceptApproval,
    canClosePhoto,
    canGoToGallery,
    SmartlyActionItems,
    SmartlyActionSubtypes,
    User,
} from '@Techedge/digitalportal-shared'
import {
    setIsApproval,
    setIsPhotographicApprovalAccept,
    setStoreId,
} from '@/app/redux/actions/activity-execution'
import ReassignDialog from '../../ActivityExecution/ReassignDialog/ReassignDialog'
import { DelegateType } from '@/app/models/assignee'

export const columns = [
    {
        field: 'storeName',
        header: 'modals.new.activity.section.distribution.table.store.name',
        filterType: LUX_INPUT_TYPE.text,
    },
    {
        field: 'storeId',
        header: 'modals.new.activity.section.distribution.table.store.id',
        filterType: LUX_INPUT_TYPE.text,
    },
    {
        field: 'status',
        header: 'modals.new.activity.section.distribution.table.status',
        template: (rowData) => {
            return (
                <LuxBadgeLabel
                    label={rowData.statusConfig.label}
                    color={rowData.statusConfig.color}
                />
            )
        },
        sortable: true,
        width: 110,
        filterType: LUX_INPUT_TYPE.select,
        appendTo: 'self' as 'self',
    },
    {
        field: '',
        template: LUX_TEMPLATE_TYPE.actions,
    },
]

export interface ActionApprovalProps {
    user: User
    activitySummaryChartData: Record<string, ActivitySummaryChartData>
    approvalStoreList: StoreProps[]
    projectDetails: any
    selectedStores: any[]
    showReassignApproval: boolean
    setSelectedStores: (stores: StoreProps[]) => void
    showStoresDetail: () => void
    onClose: () => void
    setIsApproval: (value: boolean) => void
    setStoreId: (storeId: string) => void
    setIsPhotographicApprovalAccept: (value: boolean) => void
}

const ActionApproval = (props: ActionApprovalProps) => {
    const {
        approvalStoreList,
        projectDetails,
        selectedStores,
        showReassignApproval,
        setSelectedStores,
        showStoresDetail,
        setIsApproval,
        setStoreId,
        setIsPhotographicApprovalAccept,
    } = props

    const componentClass = 'smart-modals-new-activity-approval-action'

    const intl = useIntl()

    const [sortOrder, setSortOrder] = useState(1)
    const [localStoreList, setLocalStoreList] = useState([])

    const [openReassignDialog, setOpenReassignDialog] = useState<boolean>(false)
    const [filters, setFilters] = useState({
        storeName: {
            value: '',
            matchMode: FilterMatchMode.CONTAINS,
        },
        status: {
            value: undefined,
            matchMode: FilterMatchMode.STARTS_WITH,
            options: [],
        },
    })

    const initFilters = () => {
        const statusList = approvalStoreList
            ? approvalStoreList.map((store) =>
                  intl.formatMessage({
                      id: `smart.project.status.${store.status}`,
                      defaultMessage: store.status,
                  })
              )
            : []
        setFilters({
            storeName: {
                value: '',
                matchMode: FilterMatchMode.CONTAINS,
            },
            status: {
                value: undefined,
                matchMode: FilterMatchMode.STARTS_WITH,
                options: statusList.filter((item, pos) => statusList.indexOf(item) == pos),
            },
        })
    }

    useEffect(() => {
        initFilters()
    }, [])

    useEffect(() => {
        setLocalStoreList(
            approvalStoreList?.map((store) => {
                const data: any = {
                    ...store,
                    disabled:
                        [
                            SmartlyActionSubtypes.PICTURE_MGMT,
                            SmartlyActionSubtypes.SERVICE_REQUEST,
                        ].includes(projectDetails?.type as SmartlyActionSubtypes) &&
                        !store.isEnabledApprove,
                    status: intl.formatMessage({
                        id: `smart.project.status.${store.status}`,
                        defaultMessage: store.status,
                    }),
                    statusConfig: {
                        label: intl.formatMessage({
                            id: `smart.project.status.${store.status}`,
                            defaultMessage: store.status,
                        }),
                        color: statusColors[store.status],
                        key: store.status,
                    },
                }
                try {
                    if (
                        canClosePhoto(projectDetails?.projectType, projectDetails) ||
                        canGoToGallery(projectDetails?.projectType, projectDetails) ||
                        canAcceptApproval(projectDetails?.projectType, projectDetails)
                    ) {
                        data.checkboxDisabled = !store.canAcceptApproval
                    }
                } catch {}
                return data
            })
        )
    }, [approvalStoreList])

    const onChangeFilter = (value: any, field: string) =>
        setFilters({
            ...filters,
            [field]: { ...filters[field], value: value },
        })

    const onSelectedRows = (value) => {
        let newValue = value
        if (
            canClosePhoto(projectDetails?.projectType, projectDetails) ||
            canGoToGallery(projectDetails?.projectType, projectDetails)
        ) {
            newValue = newValue.filter(
                (v) => !v.hasOwnProperty('canAcceptApproval') || v.canAcceptApproval
            )
        }
        setSelectedStores(newValue)
    }

    const goToDetails = (store: StoreProps) => {
        if (
            canClosePhoto(projectDetails?.projectType, projectDetails) ||
            canGoToGallery(projectDetails?.projectType, projectDetails)
        ) {
            if (!store.canAcceptApproval) {
                onGoToGallery(store.storeId)
                return
            }
            setIsPhotographicApprovalAccept(true)
            setSelectedStores([store])
            setStoreId(store.storeId)
            setIsApproval(false)
            return
        }
        setSelectedStores([store])
        showStoresDetail()
    }

    const openSelected = () => {
        showStoresDetail()
    }

    const onSort = (e) => {
        setSortOrder((value) => (value === 1 ? -1 : 1))
        setLocalStoreList((value) =>
            value.sort((a, b) => {
                if (a.status.label < b.status.label) {
                    return sortOrder
                }
                return -sortOrder
            })
        )
    }

    const onDownloadDataList = (dataList) => {
        downloadExcelFromList(dataList, 'exported-table')
    }

    const onGoToGallery = (storeId: string) => {
        let url = `/gallery/detail/${projectDetails._id}?selectedStores=${storeId}`
        window.location.href = window.location.origin + url
    }
    const transformDatalist = (data) => {
        const transform = {
            string: (value) => value || '',
            arraySize: (value) => (value ? value.length : 0),
        }

        return data.map((store) => ({
            storeId: transform.string(store.storeId),
            storeName: transform.string(store.storeName),
            status: transform.string(store.status),
            members: transform.string(store.members),
            visualizations: transform.arraySize(store.visualizations),
            confirmReading: transform.arraySize(store.confirmReading),
        }))
    }

    return (
        <div className={componentClass}>
            <ActivitySummaryChart />
            <div className={`${componentClass}__store-list-container`}>
                {(canClosePhoto(projectDetails?.projectType, projectDetails) ||
                    canGoToGallery(projectDetails?.projectType, projectDetails)) && (
                    <div className={`${componentClass}__accept-disclaimer`}>
                        <LuxIcon icon="info" size={14} />
                        <div className="text-mini">
                            {intl.formatMessage({
                                id: 'modals.new.activity.approval.photographic.accept.disclaimer',
                                defaultMessage:
                                    'Important! To enable approval functionalities on photos, it is first necessary to take on/"accept" the related store',
                            })}
                        </div>
                    </div>
                )}
                <LuxDataTable
                    label={intl.formatMessage({
                        id: 'modals.new.activity.section.distribution.store.list',
                        defaultMessage: 'Store List',
                    })}
                    selectionMode={'multiple'}
                    columns={columns.map((column) => ({
                        ...column,
                        header: column.header
                            ? intl.formatMessage({
                                  id: column.header,
                                  defaultMessage: column.header.split('.').pop(),
                              })
                            : '',
                    }))}
                    dataSet={localStoreList}
                    scrollable
                    selectedRows={selectedStores}
                    setSelectedRows={(value) => onSelectedRows(value)}
                    totalRecords={approvalStoreList?.length}
                    dataKey="storeId"
                    actionsList={[
                        {
                            icon: 'caret-right',
                            command: (e: StoreProps) => goToDetails(e),
                        },
                    ]}
                    columnFilters={filters}
                    onFilterChange={onChangeFilter}
                    onSort={onSort}
                    sortOrder={sortOrder}
                    sortField="status"
                    onCloseFilters={initFilters}
                    containerClassName={`${componentClass}__datatable`}
                    labels={{
                        filter: intl.formatMessage({
                            id: 'modals.new_resource.distribution.store_list.filter',
                            defaultMessage: 'filter',
                        }),
                        rearrange: intl.formatMessage({
                            id: 'modals.new_resource.distribution.store_list.rearrange',
                            defaultMessage: 'rearrange',
                        }),
                        of: intl.formatMessage({
                            id: 'modals.new_resource.distribution.store_list.labels.of',
                            defaultMessage: 'of',
                        }),
                        selected: intl.formatMessage({
                            id: 'modals.new_resource.distribution.store_list.labels.selected',
                            defaultMessage: 'selected',
                        }),
                        emptyMessage: intl.formatMessage({
                            id: 'modals.new_resource.distribution.store_list.labels.emptyMessage',
                            defaultMessage: 'No results to show.',
                        }),
                    }}
                    onDownload={() => {
                        onDownloadDataList(transformDatalist(localStoreList))
                    }}
                />
            </div>
            <div className={`${componentClass}__cta-container`}>
                {projectDetails?.type !== SmartlyActionSubtypes.PICTURE_MGMT && (
                    <LuxButton
                        variant={'outlined'}
                        onClick={openSelected}
                        disabled={selectedStores?.length === 0}
                        text={intl.formatMessage({
                            id: 'modals.new.activity.approval.action.open.selected',
                            defaultMessage: 'Open selected',
                        })}
                    />
                )}
                {showReassignApproval && (
                    <LuxButton
                        variant={'outlined'}
                        text={intl.formatMessage({
                            id: 'ph2.modals.news_execution.button.reassign_approver',
                            defaultMessage: 'Reassign approver',
                        })}
                        onClick={() => setOpenReassignDialog(true)}
                    />
                )}
            </div>
            <ReassignDialog
                visible={openReassignDialog}
                projectDetails={projectDetails}
                storesList={localStoreList}
                delegateWhat={DelegateType.approver}
                hideReassignmentSize
                onHide={() => setOpenReassignDialog(false)}
            />
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    activitySummaryChartData:
        state[NEW_ACTIVITY_APPROVAL_REDUCER_NAMESPACE].activitySummaryChartData,
    approvalStoreList: state[NEW_ACTIVITY_APPROVAL_REDUCER_NAMESPACE].approvalStoreList,
    projectDetails: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].projectDetails,
    selectedStores: state[NEW_ACTIVITY_APPROVAL_REDUCER_NAMESPACE].selectedStores,
    user: state.userReducer.user,
})

const mapDispatchToProps = (dispatch: any) => ({
    setSelectedStores: (stores: StoreProps[]) => dispatch(setSelectedStores(stores)),
    setIsApproval: (value) => {
        dispatch(setIsApproval(value))
    },
    setStoreId: (storeId) => {
        dispatch(setStoreId(storeId))
    },
    setIsPhotographicApprovalAccept: (value) => {
        dispatch(setIsPhotographicApprovalAccept(value))
    },
})

export default connect(mapStateToProps, mapDispatchToProps)(ActionApproval)
